"use client";

import { useState, useEffect } from "react";
import {
  InstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { getInstantSearchClient, INDEXES } from "@/lib/meilisearch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, AlertCircle } from "lucide-react";
import { SearchResults } from "./SearchResults";
import { SearchFilters } from "./SearchFilters";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SearchStateManager } from "./SearchStateManager";
import { SearchStateRestorer } from "./SearchStateRestorer";


function CustomSearchBox() {
  const { query, refine } = useSearchBox();

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative w-full">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search creators..."
          value={query}
          onChange={(e) => refine(e.currentTarget.value)}
          className="pl-8"
        />
      </div>
    </div>
  );
}

interface SearchInterfaceProps {
  campaign?: any;
  campaignId?: string;
  canInvite?: boolean;
  inviteBasePath?: string;
}

export function SearchInterface({
  campaign,
  campaignId,
  canInvite,
  inviteBasePath,
}: SearchInterfaceProps) {
  const [hasError, setHasError] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchClient, setSearchClient] = useState<ReturnType<
    typeof getInstantSearchClient
  > | null>(null);

  useEffect(() => {
    setIsClient(true);

    // Create the search client on the client side
    try {
      const client = getInstantSearchClient();
      setSearchClient(client);
    } catch (error) {
      console.error("Failed to create search client:", error);
      setHasError(true);
    }
  }, []);

  // Don't render until we have a valid client
  if (!isClient || !searchClient) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading search...</p>
        </div>
      </div>
    );
  }

  // Show error if client creation failed
  if (hasError) {
    return (
      <div className="space-y-6">
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Search not configured:</strong> Unable to create search
            client. Please check your Meilisearch configuration.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Main search interface - now with better error handling
  return (
    <InstantSearch
      searchClient={searchClient as any}
      indexName={INDEXES.CREATORS}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <SearchStateManager>
        <SearchStateRestorer />
        <div className="space-y-6">
          {/* Search Box and Mobile Filters Button */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <CustomSearchBox />
            </div>
            
          {/* Mobile Filters Button */}
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden w-full sm:w-auto">
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[350px] overflow-y-auto">
              <div className="mt-4">
                <SearchFilters />
              </div>
            </SheetContent>
          </Sheet>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            {/* Filters - Hidden on mobile, shown in sheet */}
            <div className="hidden lg:block lg:col-span-1" data-guide-step="filters">
              <div className="sticky top-8 h-[calc(100vh-12rem)]">
                <SearchFilters />
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-3" data-guide-step="creators-list">
              <SearchResults
                campaign={campaign}
                campaignId={campaignId}
                canInvite={canInvite}
                inviteBasePath={inviteBasePath}
              />
            </div>
          </div>
        </div>
      </SearchStateManager>
    </InstantSearch>
  );
}
